import React from 'react';
import { CloudProvider } from '../../contexts/ProviderContext';
import { PROVIDER_COLORS } from '../../utils/providerColors';
import { ProviderEmptyState } from './ProviderEmptyState';
import { formatCurrencyAbbreviated, CurrencyCode } from '../../utils/currency';

/**
 * Provider Summary Cards component
 * Implements REQ-012-HYBRID: Display 3 provider summary cards (AWS, Azure, GCP)
 * prominently at the top of the dashboard in a responsive 3-column grid.
 *
 * Each card shows:
 * - Provider logo/icon and name with brand color
 * - Total cost (formatted as currency)
 * - MoM trend indicator (↑5.2% in red or ↓3.1% in green)
 * - Waste percentage
 * - Savings opportunity amount
 * - Top 3 services by cost
 * - Connection status (green/red dot)
 *
 * Responsive behavior:
 * - Desktop (lg+): 3-column grid (All Providers) or full width (single provider)
 * - Tablet (sm-lg): 2 columns then wrap (All Providers) or full width (single provider)
 * - Mobile (<sm): single column stack
 *
 * REQ-012: Provider selection behavior:
 * - "All Providers": show all 3 cards side-by-side with equal sizing (3-column grid)
 * - Specific provider (AWS/Azure/GCP): expand that card to full width, hide others
 * - Smooth CSS transitions between views
 */

/**
 * Brand colors for each cloud provider.
 * @deprecated Import from '../../utils/providerColors' instead.
 * Re-exported here for backward compatibility.
 */
export const PROVIDER_BRAND_COLORS = PROVIDER_COLORS;

/** Per-provider metrics data passed from the dashboard */
export interface ProviderMetricsData {
  /** Total cost for this provider */
  totalCost: number;
  /** Month-over-month trend percentage (e.g. 5.2 for 5.2%) */
  momTrendPercentage: number;
  /** Direction of MoM trend */
  momTrendDirection: 'up' | 'down';
  /** Waste percentage (0-100) */
  wastePercentage: number;
  /** Savings opportunity amount in dollars */
  savingsOpportunity: number;
  /** Top 3 services by cost */
  topServices: Array<{ name: string; cost: number }>;
  /** Whether this provider has an active connection */
  connected: boolean;
  /** Currency code for this provider (USD for AWS/GCP, INR for Azure, or USD when multi-provider) */
  currency: CurrencyCode;
}

export interface ProviderSummaryData {
  provider: 'aws' | 'azure' | 'gcp';
  name: string;
  color: string;
  icon: React.ReactNode;
}

interface ProviderSummaryCardsProps {
  /** Currently active provider filter from ProviderContext */
  activeProvider?: CloudProvider;
  /** Optional callback when a provider card is clicked */
  onProviderClick?: (provider: CloudProvider) => void;
  /** Optional additional CSS class */
  className?: string;
  /** Per-provider metrics data keyed by provider id */
  providerMetrics?: Partial<Record<'aws' | 'azure' | 'gcp', ProviderMetricsData>>;
}

/** SVG Icons for each cloud provider (larger, suitable for cards) */
const ProviderCardIcons: Record<'aws' | 'azure' | 'gcp', React.ReactNode> = {
  aws: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 01-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 01-.287-.375 6.18 6.18 0 01-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.032-.863.104-.296.072-.583.16-.863.272a2.287 2.287 0 01-.28.104.488.488 0 01-.127.023c-.112 0-.168-.08-.168-.247v-.391c0-.128.016-.224.056-.28a.597.597 0 01.224-.167c.28-.144.616-.264 1.01-.36a4.84 4.84 0 011.244-.152c.95 0 1.644.216 2.091.647.44.43.662 1.085.662 1.963v2.586zm-3.24 1.214c.263 0 .535-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.048-.191.08-.423.08-.694v-.335a6.66 6.66 0 00-.735-.136 6.02 6.02 0 00-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.144 0-.24-.024-.304-.08-.064-.048-.12-.16-.168-.311L7.586 5.55a1.398 1.398 0 01-.072-.32c0-.128.064-.2.191-.2h.783c.151 0 .255.025.31.08.065.048.113.16.16.312l1.342 5.284 1.245-5.284c.04-.16.088-.264.151-.312a.549.549 0 01.32-.08h.638c.152 0 .256.025.32.08.063.048.12.16.151.312l1.261 5.348 1.381-5.348c.048-.16.104-.264.16-.312a.52.52 0 01.311-.08h.743c.128 0 .2.064.2.2 0 .04-.009.08-.017.128a1.137 1.137 0 01-.056.2l-1.923 6.17c-.048.16-.104.264-.168.312a.549.549 0 01-.303.08h-.687c-.151 0-.255-.024-.32-.08-.063-.056-.119-.16-.15-.32l-1.238-5.148-1.23 5.14c-.04.16-.087.264-.15.32-.065.056-.177.08-.32.08zm10.256.215c-.415 0-.83-.048-1.229-.143-.399-.096-.71-.2-.918-.32-.128-.071-.215-.151-.247-.223a.563.563 0 01-.048-.224v-.407c0-.167.064-.247.183-.247.048 0 .096.008.144.024.048.016.12.048.2.08.271.12.566.215.878.279.319.064.63.096.95.096.502 0 .894-.088 1.165-.264a.86.86 0 00.415-.758.777.777 0 00-.215-.559c-.144-.151-.415-.287-.806-.415l-1.157-.36c-.583-.183-1.014-.454-1.277-.813a1.902 1.902 0 01-.4-1.158c0-.335.073-.63.216-.886.144-.255.335-.479.575-.654.24-.184.51-.32.83-.415a3.57 3.57 0 011.013-.144c.183 0 .375.008.559.032.191.024.367.056.543.096.168.04.327.08.479.128.151.048.271.096.367.144a.724.724 0 01.263.2.439.439 0 01.072.263v.375c0 .168-.064.256-.184.256a.83.83 0 01-.303-.096 3.652 3.652 0 00-1.532-.311c-.455 0-.815.071-1.062.223-.248.152-.375.383-.375.694 0 .224.08.415.24.567.16.152.454.303.87.44l1.133.358c.574.184.99.44 1.237.767.248.327.367.702.367 1.117 0 .343-.072.655-.207.926-.144.272-.336.511-.583.703-.248.2-.543.343-.886.447-.36.111-.734.167-1.142.167z" />
    </svg>
  ),
  azure: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5.483 21.3H14.3l4.818-8.1-5.35-6.3L5.483 21.3zm8.4-17.7l-5.1 6.6h6.6l-3.9 11.1L19.2 12H12.6l5.1-8.4h-3.817z" />
    </svg>
  ),
  gcp: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.19 2.38a9.344 9.344 0 00-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.111-.247 10.941l.006-.007-.007.003a6.542 6.542 0 005.181 2.584h8.347a6.544 6.544 0 006.344-4.907 6.537 6.537 0 00-1.895-6.281A9.34 9.34 0 0012.19 2.38zm-.358 4.146c1.244-.04 2.518.368 3.537 1.202a5.2 5.2 0 011.63 2.376l-2.093 1.209a2.847 2.847 0 00-3.074-1.883 2.853 2.853 0 00-2.248 2.262l-2.092 1.209a5.159 5.159 0 01.602-4.185 5.207 5.207 0 013.738-2.19zm-5.59 5.63l2.092-1.21a2.852 2.852 0 002.341 2.932 2.851 2.851 0 002.98-1.313l2.093 1.209a5.159 5.159 0 01-3.168 2.64 5.207 5.207 0 01-4.426-.86l-.006.003a4.193 4.193 0 01-1.906-3.4z" />
    </svg>
  ),
};

/** Provider card configuration */
const PROVIDER_CARDS: ProviderSummaryData[] = [
  {
    provider: 'aws',
    name: 'Amazon Web Services',
    color: PROVIDER_COLORS.aws,
    icon: ProviderCardIcons.aws,
  },
  {
    provider: 'azure',
    name: 'Microsoft Azure',
    color: PROVIDER_COLORS.azure,
    icon: ProviderCardIcons.azure,
  },
  {
    provider: 'gcp',
    name: 'Google Cloud Platform',
    color: PROVIDER_COLORS.gcp,
    icon: ProviderCardIcons.gcp,
  },
];

/**
 * ProviderSummaryCards displays cloud provider cards in a responsive layout.
 * Cards are placed prominently at the top of the dashboard.
 *
 * REQ-012: Provider view modes:
 * - "All Providers" (activeProvider === 'all' or undefined): show all 3 cards
 *   side-by-side in a 3-column grid with equal sizing.
 * - Specific provider (activeProvider === 'aws' | 'azure' | 'gcp'): expand the
 *   selected provider's card to full container width, hide other provider cards.
 *
 * The grid uses CSS Grid with responsive columns:
 * - All Providers: `grid-cols-1 sm:grid-cols-2 lg:grid-cols-3`
 * - Single Provider: `grid-cols-1` (full width)
 *
 * Smooth transitions between views via CSS transition classes.
 *
 * WCAG 2.1 AA compliant:
 * - Semantic HTML with role="article" for each card
 * - Keyboard accessible (clickable cards use button semantics)
 * - Sufficient color contrast
 * - aria-labels for screen readers
 * - Screen reader-only text for trend direction and connection status
 * - aria-live region announces view changes to assistive technologies
 */
export const ProviderSummaryCards: React.FC<ProviderSummaryCardsProps> = ({
  activeProvider = 'all',
  onProviderClick,
  className = '',
  providerMetrics,
}) => {
  const isSingleProvider = activeProvider !== 'all';

  // REQ-012: When "All Providers" selected, show all 3 cards side-by-side with equal sizing.
  // When specific provider selected, filter to show only that provider's card at full width.
  const cardsToShow = isSingleProvider
    ? PROVIDER_CARDS.filter((card) => card.provider === activeProvider)
    : PROVIDER_CARDS;

  // Determine grid layout: full width for single provider, 3-column for all providers
  const gridClassName = isSingleProvider
    ? 'grid grid-cols-1'
    : 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section
      className={`${gridClassName} transition-all duration-300 ease-in-out ${className}`}
      aria-label="Cloud provider summary"
      aria-live="polite"
      aria-atomic="true"
    >
      {cardsToShow.map((card) => (
        <ProviderCard
          key={card.provider}
          data={card}
          metrics={providerMetrics?.[card.provider]}
          onClick={onProviderClick ? () => onProviderClick(card.provider) : undefined}
          expanded={isSingleProvider}
        />
      ))}
    </section>
  );
};

/** Individual provider card */
interface ProviderCardProps {
  data: ProviderSummaryData;
  metrics?: ProviderMetricsData;
  onClick?: () => void;
  /** Whether this card is expanded to full width (single provider view) */
  expanded?: boolean;
}

const ProviderCard: React.FC<ProviderCardProps> = ({ data, metrics, onClick, expanded = false }) => {
  const trendColor = metrics?.momTrendDirection === 'up' ? 'text-rose-600' : 'text-emerald-600';
  const trendArrow = metrics?.momTrendDirection === 'up' ? '↑' : '↓';
  const trendLabel = metrics?.momTrendDirection === 'up' ? 'increase' : 'decrease';
  
  // CRITICAL FIX: Use currency from API response, not hardcoded fallback
  // The backend returns the correct currency (INR for Azure when viewing alone, USD when multi-provider)
  const currency: CurrencyCode = metrics?.currency as CurrencyCode || 'USD';

  const cardContent = (
    <>
      {/* Color accent bar positioned at TOP of card for all providers (AWS, Azure, GCP)
          to ensure consistent visual styling. When expanded (single provider view), bar moves to left side. */}
      {!expanded && (
        <div
          className="absolute top-0 left-0 right-0 h-1.5 rounded-t-lg"
          style={{ backgroundColor: data.color }}
          aria-hidden="true"
        />
      )}
      {expanded && (
        <div
          className="h-full w-1.5 rounded-l-lg"
          style={{ backgroundColor: data.color }}
          aria-hidden="true"
        />
      )}

      <div className={`${expanded ? 'flex-1' : 'pt-1.5'} p-4`}>
        {/* Header: Provider icon + name + connection status */}
        <div className="flex items-center gap-2.5">
          {/* Provider icon */}
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${data.color}15` }}
          >
            <span style={{ color: data.color }}>{data.icon}</span>
          </div>

          {/* Provider name */}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground">
              {data.provider.toUpperCase()}
            </h3>
            <p className="truncate text-[11px] text-muted-foreground">
              {data.name}
            </p>
          </div>

          {/* Connection status indicator */}
          {metrics !== undefined && (
            <div className="flex flex-shrink-0 items-center gap-1.5">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${
                  metrics.connected ? 'bg-emerald-500' : 'bg-rose-500'
                }`}
                aria-hidden="true"
              />
              <span className="text-xs text-muted-foreground">
                {metrics.connected ? 'Connected' : 'Disconnected'}
              </span>
              <span className="sr-only">
                {metrics.connected
                  ? `${data.provider.toUpperCase()} is connected`
                  : `${data.provider.toUpperCase()} is disconnected`}
              </span>
            </div>
          )}
        </div>

        {/* Metrics section - uses horizontal layout when expanded */}
        {/* EXPANDED (single provider selected) renders a labelled metric strip
            plus a bounded services panel, rather than the old three-across
            grid. At full page width that grid put cost, waste/savings and
            services in three cells a hand's width apart, so nothing read as
            belonging together - the "scattered numbers" complaint. Labels above
            values is the layout cost consoles use, and it holds at any width.

            COLLAPSED (3-up) is deliberately untouched below: the card is narrow
            there and the stacked justify-between rows already read correctly. */}
        {metrics && expanded && (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Total spend</p>
                <p className="mt-0.5 text-2xl font-semibold tracking-tight text-foreground">
                  {formatCurrencyAbbreviated(metrics.totalCost, currency)}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Month over month</p>
                <p className={`mt-0.5 text-2xl font-semibold tracking-tight ${trendColor}`}>
                  {trendArrow}{metrics.momTrendPercentage.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Waste</p>
                <p className="mt-0.5 text-2xl font-semibold tracking-tight text-foreground">
                  {metrics.wastePercentage.toFixed(0)}%
                </p>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Savings opportunity</p>
                <p className="mt-0.5 text-2xl font-semibold tracking-tight text-emerald-700 dark:text-emerald-400">
                  {formatCurrencyAbbreviated(metrics.savingsOpportunity, currency)}
                </p>
              </div>
            </div>

            {metrics.topServices.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/20 p-3">
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Top services
                </p>
                <ul className="space-y-1.5" aria-label={`Top services for ${data.provider.toUpperCase()}`}>
                  {metrics.topServices.slice(0, 3).map((service) => (
                    <li key={service.name} className="flex items-center justify-between gap-3 text-xs">
                      <span className="truncate text-foreground/80">{service.name}</span>
                      <span className="flex-shrink-0 font-medium tabular-nums text-foreground">
                        {formatCurrencyAbbreviated(service.cost, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {metrics && !expanded && (
          <div className={`mt-3 ${expanded ? 'grid grid-cols-1 gap-3 sm:grid-cols-3' : 'space-y-2.5'}`}>
            {/* Total cost + MoM trend */}
            <div>
              {/* justify-between only in the COLLAPSED (3-up) layout, where the
                  card is narrow and pushing value left / trend right reads as a
                  tidy row. Expanded, the card spans the full page and each grid
                  cell is a third of it — justify-between then flung the value
                  and its trend to opposite ends of that third, which is the
                  "scattered numbers" effect. Grouped with a small gap instead. */}
              <div className={`flex items-baseline ${expanded ? 'gap-2' : 'justify-between'}`}>
                <span
                  className="text-xl font-semibold tracking-tight text-foreground"
                  aria-label={`Total cost: ${formatCurrencyAbbreviated(metrics.totalCost, currency)}`}
                >
                  {formatCurrencyAbbreviated(metrics.totalCost, currency)}
                </span>
                <span
                  className={`text-sm font-medium ${trendColor}`}
                  aria-label={`Month over month ${trendLabel}: ${metrics.momTrendPercentage.toFixed(1)} percent`}
                >
                  {trendArrow}{metrics.momTrendPercentage.toFixed(1)}%
                  <span className="ml-1 text-xs text-muted-foreground">MoM</span>
                </span>
              </div>
            </div>

            {/* Waste % and Savings opportunity — same collapsed/expanded split
                as the row above, for the same reason. */}
            <div className={`flex items-center text-sm ${expanded ? 'gap-4' : 'justify-between'}`}>
              <span className="flex items-center gap-1">
                <span
                  className="inline-block h-2 w-2 rounded-full bg-rose-500"
                  aria-hidden="true"
                />
                <span className="text-foreground/80">
                  {metrics.wastePercentage.toFixed(0)}% waste
                </span>
              </span>
              <span className="font-medium text-emerald-700 dark:text-emerald-400">
                <span aria-label={`Savings opportunity: ${formatCurrencyAbbreviated(metrics.savingsOpportunity, currency)}`}>
                  💰 {formatCurrencyAbbreviated(metrics.savingsOpportunity, currency)} savings
                </span>
              </span>
            </div>

            {/* Top 3 services */}
            {metrics.topServices.length > 0 && (
              <div className={`${expanded ? '' : 'border-t border-border pt-3 dark:border-border'}`}>
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Top Services
                </p>
                {/* max-w when expanded: a service name and its cost are a
                    label→value pair, so justify-between is right — but across a
                    full-width third of the page they end up a hand's width
                    apart and stop reading as one row. Capping the list keeps
                    the pair visually associated. */}
                <ul
                  className={`space-y-1 ${expanded ? 'max-w-sm' : ''}`}
                  aria-label={`Top services for ${data.provider.toUpperCase()}`}
                >
                  {metrics.topServices.slice(0, 3).map((service) => (
                    <li
                      key={service.name}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="truncate text-foreground/80">
                        {service.name}
                      </span>
                      <span className="ml-2 flex-shrink-0 font-medium text-foreground">
                        {formatCurrencyAbbreviated(service.cost, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Fallback when no metrics available - show compact empty state with Configure link */}
        {!metrics && (
          <div className="mt-4">
            <ProviderEmptyState provider={data.provider} compact />
          </div>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`relative w-full overflow-hidden rounded-lg border border-border bg-card text-left shadow-sm transition-all duration-300 ease-in-out hover:shadow-md hover:border-border focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:border-border dark:bg-card dark:hover:border-border ${expanded ? 'flex flex-row' : ''}`}
        role="article"
        aria-label={`${data.provider.toUpperCase()} provider summary card. ${
          metrics
            ? `Total cost ${formatCurrencyAbbreviated(metrics.totalCost, currency)}, ${trendArrow}${metrics.momTrendPercentage.toFixed(1)}% month over month, ${metrics.wastePercentage.toFixed(0)}% waste, ${formatCurrencyAbbreviated(metrics.savingsOpportunity, currency)} savings opportunity, ${metrics.connected ? 'connected' : 'disconnected'}.`
            : 'No data available.'
        }${expanded ? ' Currently showing expanded view.' : ' Click to filter by ' + data.name}`}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-all duration-300 ease-in-out hover:shadow-md dark:border-border dark:bg-card ${expanded ? 'flex flex-row' : ''}`}
      role="article"
      aria-label={`${data.provider.toUpperCase()} provider summary card. ${
        metrics
          ? `Total cost ${formatCurrencyAbbreviated(metrics.totalCost, currency)}, ${trendArrow}${metrics.momTrendPercentage.toFixed(1)}% month over month, ${metrics.wastePercentage.toFixed(0)}% waste, ${formatCurrencyAbbreviated(metrics.savingsOpportunity, currency)} savings opportunity, ${metrics.connected ? 'connected' : 'disconnected'}.`
          : 'No data available.'
      }${expanded ? ' Currently showing expanded view.' : ''}`}
    >
      {cardContent}
    </div>
  );
};
